import React, { useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { BooksItem } from './books_item';
import PAGE_TITLES from '../constants/pageTitles';
import BookPreview from './BookPreview';
import { BooksIn } from './books_in';
import { PRODUCT_PAGES } from '../constants/routes';
import { clearCoverPictures, clearPictures } from '../redux/reducers/picturesReducer';
import Spinner from '../styles/images/spinner.gif';
import { ExtraPhotoBookPremium } from './ExtraPhotoBookPremium';
import { ExtraPhotoMosaic } from './ExtraPhotoMosaic';
import { ExtraPostcards } from './ExtraPostcards';
import '../styles/books.scss';
import { validator } from '../containers/MainElemRender';
import { useScrollTop } from '../helpers/useScrollTop';

const massWithExtraScreen = [
  'photo-book-RU',
  'photo-book-RU-light',
  'photo-book-RU-slim',
  'photo-book-RU-soft',
  'postcards-product-self-sending',
  'postcards-product-send-for-me',
  'photo-mosaic',
];

export const TITLES = {
  [PRODUCT_PAGES.FOTOS]: 'Фотографии',
  [PRODUCT_PAGES.FOTOBOOKS]: 'ФотоКниги',
  [PRODUCT_PAGES.CALENDARS]: 'Календари',
  [PRODUCT_PAGES.GIFT_FACTORY]: 'Фабрика Подарков',
  [PRODUCT_PAGES.POSTCARDS]: 'Открытки',
  [PRODUCT_PAGES.INTERIOR_DECOR]: 'Декор Интерьера',
  [PRODUCT_PAGES.MAGNETS]: 'Магниты',
  [PRODUCT_PAGES.PHOTO_SOUVENIRS]: 'Фото Сувениры',
  [PRODUCT_PAGES.CLOTHING_WITH_PHOTO]: 'Одежда с Фото',
  [PRODUCT_PAGES.GIFT_CERTIFICATES]: 'Подарочные сертификаты',
};

const setPageTitle = page => {
  switch (page) {
    case PRODUCT_PAGES.FOTOBOOKS: {
      document.title = PAGE_TITLES.fotobooks;
      break;
    }
    case PRODUCT_PAGES.CALENDARS: {
      document.title = PAGE_TITLES.calendars;
      break;
    }
    case PRODUCT_PAGES.FOTOS: {
      document.title = PAGE_TITLES.photo;
      break;
    }
    default: {
      document.title = PAGE_TITLES.aboutUS;
    }
  }
};

const ExtraScreen = ({ productId, ...props }) => {
  switch (productId) {
    case 'photo-book-RU':
    case 'photo-book-RU-light':
    case 'photo-book-RU-slim':
    case 'photo-book-RU-soft':
      return <ExtraPhotoBookPremium {...props} productId={productId} />;
    case 'postcards-product-self-sending':
    case 'postcards-product-send-for-me':
      return <ExtraPostcards {...props} />;
    case 'photo-mosaic':
      return <ExtraPhotoMosaic {...props} />;
    default:
      return null;
  }
};

export const Books = ({ booksIn, setLoader }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { query = {}, pathname } = useLocation();
  useScrollTop();
  const { basketItemId, productTypeId, ...queryData } = query;
  const { page, id_product } = useParams();

  const [selectedItem, setSelectedItem] = useState(null);
  const [product, setProduct] = useState(null);
  const [extraScreenOptions, setExtraScreenOptions] = useState(null);

  const productTypes = useSelector(state => state.common.productTypes);
  const { images, coverImages } = useSelector(state => state.pictures.uploadedPictures);
  const products = productTypes[page];
  const isContinue = massWithExtraScreen.includes(selectedItem?.id);

  const onBackPreview = () => {
    setSelectedItem(null);
    history.push({ pathname: `/products/${page}`, query: {} });
  };

  const onBack = () => {
    const queryParams = {
      ...product,
      orientation: product.orientation,
      type: product.type,
      size: product.size,
      shilda: product.shilda,
      cover: product.cover,
      wishes: product.wishes,
    };
    history.push({ pathname, query: queryParams });
    setProduct(null);
    dispatch(clearPictures());
  };

  const onBackExtra = () => {
    setExtraScreenOptions(null);
    dispatch(clearCoverPictures());
  };

  const onAddToBasket = d => {
    if (isContinue) {
      const showAnswer = validator(product, images, coverImages);
      if (showAnswer) {
        setLoader({ showLoader: true, showAnswer });
        return;
      }
      setExtraScreenOptions(d);
    } else booksIn.addToBasket(d, product, basketItemId);
  };

  const onAddToBasketExtra = (d, p) => {
    booksIn.addToBasket(d, { ...product, ...p }, basketItemId);
  };

  const onItemClick = item => () => {
    if (item.id === 'gift-cert-RU') {
      history.push('/products/certificates/gift-cert-RU');
      return;
    }
    history.push(`/products/${page}/${item.id}`);
    setSelectedItem(item);
  };

  useEffect(() => {
    if (selectedItem && !id_product) setSelectedItem(null);
  }, [id_product, selectedItem]);

  useEffect(() => {
    if (!products) history.push('/');
  }, [products]);

  useEffect(() => {
    const productToEdit = products?.find(p => p.id === productTypeId);
    if (productTypeId && productToEdit) {
      setSelectedItem(productToEdit);
      setProduct({ productTypeId, ...queryData });
    } else if (selectedItem) setSelectedItem(null);
    if (product) setProduct(null);
    if (extraScreenOptions) setExtraScreenOptions(null);
    setPageTitle(page);
    return () => {
      dispatch(clearPictures());
      document.title = PAGE_TITLES.aboutUS;
    };
  }, [page, id_product]);

  useEffect(() => {
    if (!selectedItem && id_product && products?.length) {
      const productFromUrl = products.find(({ id }) => id === id_product);
      if (productFromUrl) setSelectedItem(productFromUrl);
      else history.push('/');
    } else if (!id_product) {
      setProduct(null);
      setSelectedItem(null);
    }
  }, [id_product, products, selectedItem]);

  if (extraScreenOptions) {
    return (
      <ExtraScreen
        productId={selectedItem?.id}
        onBack={onBackExtra}
        max={product?.max}
        addToBasket={onAddToBasketExtra}
        {...extraScreenOptions}
      />
    );
  }

  if (product) {
    return (
      <BooksIn
        hasComment={booksIn.hasComment}
        hasCopies={booksIn.hasCopies}
        hasCover={booksIn.hasCover}
        addToBasket={onAddToBasket}
        title={booksIn.title(product)}
        product={product}
        onBack={onBack}
        productId={selectedItem?.id}
        isContinue={isContinue}
      />
    );
  }

  if (selectedItem) {
    return <BookPreview item={selectedItem} onSelectProduct={setProduct} onBack={onBackPreview} />;
  }

  if (!selectedItem && id_product) {
    return (
      <div className="books-page__loader">
        <img src={Spinner} className="gifSpinner" alt="" />
      </div>
    );
  }

  return (
    <div className="books-page">
      <h1 className="books-page-title">{TITLES[page] || ''}</h1>
      <div className="block-photobooks">
        {products?.map(item => (
          <BooksItem key={item.id} item={item} onItemClick={onItemClick(item)} />
        ))}
      </div>
    </div>
  );
};
